import React from "react";
import { ClipboardList, Calendar, User, ArrowRight } from "lucide-react";

interface Order {
  id: string;
  type: "test" | "procedure" | "referral";
  description: string;
  date: string;
  status: "pending" | "completed" | "cancelled";
  orderedBy: string;
  provider?: string;
  notes?: string;
}

const mockOrders: Order[] = [
  {
    id: "1",
    type: "test",
    description: "MRI - Brain",
    date: "2024-03-20",
    status: "pending",
    orderedBy: "Dr. Sarah Johnson",
    provider: "City Imaging Center",
    notes: "Patient to fast for 6 hours prior to procedure",
  },
  {
    id: "2",
    type: "referral",
    description: "Cardiology Consultation",
    date: "2024-03-18",
    status: "pending",
    orderedBy: "Dr. Michael Chen",
    provider: "Dr. Robert Smith, Cardiology",
    notes: "Follow-up for abnormal EKG results",
  },
  {
    id: "3",
    type: "procedure",
    description: "Colonoscopy",
    date: "2024-03-15",
    status: "completed",
    orderedBy: "Dr. Emily Rodriguez",
    provider: "Gastroenterology Associates",
    notes: "Procedure completed successfully",
  },
  {
    id: "4",
    type: "test",
    description: "Blood Work - Thyroid Panel",
    date: "2024-03-10",
    status: "completed",
    orderedBy: "Dr. Sarah Johnson",
    provider: "LabCorp",
    notes: "Results reviewed and normal",
  },
];

export const OrdersTab: React.FC = () => {
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Completed
          </span>
        );
      case "pending":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            Pending
          </span>
        );
      case "cancelled":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            Cancelled
          </span>
        );
      default:
        return null;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "test":
        return <ClipboardList className="w-5 h-5 text-blue-500" />;
      case "procedure":
        return <ClipboardList className="w-5 h-5 text-purple-500" />;
      case "referral":
        return <ArrowRight className="w-5 h-5 text-green-500" />;
      default:
        return <ClipboardList className="w-5 h-5 text-gray-500" />;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium dark:text-white">
          Orders & Referrals
        </h2>
        <button className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90">
          <ClipboardList className="w-4 h-4 mr-2" />
          New Order
        </button>
      </div>

      <div className="space-y-4">
        {mockOrders.map((order) => (
          <div
            key={order.id}
            className="bg-white dark:bg-darkCardBackground rounded-lg border border-gray-200 dark:border-gray-700 p-4"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3">
                <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  {getTypeIcon(order.type)}
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white">
                    {order.description}
                  </h3>
                  <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mt-1">
                    <User className="w-4 h-4" />
                    <span>Ordered by {order.orderedBy}</span>
                  </div>
                  {order.provider && (
                    <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mt-1">
                      <ArrowRight className="w-4 h-4" />
                      <span>{order.provider}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-end space-y-2">
                <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                  <Calendar className="w-4 h-4" />
                  <span>{order.date}</span>
                </div>
                {getStatusBadge(order.status)}
              </div>
            </div>
            {order.notes && (
              <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
                {order.notes}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
